import {useEffect, useRef, useState} from 'react';
import styles from './Glp1PrimerCascadeBody.module.css';

/** Glp1 Primer Cascade Body — animated cascade for slide B.
 *
 * Faithful port of Nyx-iOS Glp1PrimerCascadeBody.tsx. Drives:
 *   - 3.8s staggered build (card frame → labels → bars → arrows → science)
 *   - 8s infinite loop alternating "Tonight" (orange/pink) and "On
 *     treatment" / "With weight loss" (gold) gradients per bar, with
 *     the cascade direction shifting top-to-bottom and back.
 *
 * Animation is driven by CSS @keyframes (see .module.css). The component
 * only manages WHEN the animation triggers — it fires when `isActive`
 * first becomes true. Subsequent re-activations skip the build per the
 * iOS spec ("Subsequent views in the same session should skip the build
 * animation and render the resting Tonight state immediately").
 */
export const Glp1PrimerCascadeBody = ({
  isActive,
  variant,
}: {
  isActive: boolean;
  variant: 'branded_v1' | 'compounded_v1';
}) => {
  const isCompounded = variant === 'compounded_v1';
  const [triggered, setTriggered] = useState(false);
  const hasBuiltRef = useRef(false);

  useEffect(() => {
    if (!isActive) return;
    if (!hasBuiltRef.current) {
      hasBuiltRef.current = true;
    }
    setTriggered(true);
  }, [isActive]);

  const rootClass = `${styles.root} ${triggered ? styles.triggered : ''}`;
  const cardClass = `${styles.mechanismCard}`;

  return (
    <div className={rootClass}>
      <div className={cardClass}>
        {/* Direction tags — absolutely positioned to the top-right of the
            mechanism card; opacity + Y offset driven by tag keyframes. */}
        <span className={`${styles.tag} ${styles.tagTonight}`}>Tonight</span>
        <span className={`${styles.tag} ${styles.tagOnTreatment}`}>
          {isCompounded ? 'With weight loss' : 'On treatment'}
        </span>

        {/* Row 1 — Body weight (the cause) */}
        <div className={styles.row}>
          <span className={`${styles.label} ${styles.weightLabel}`}>BODY WEIGHT</span>
          <div className={styles.barTrack}>
            <div className={`${styles.bar} ${styles.weightBar}`}>
              <span className={styles.tonightGradient} />
              <span className={`${styles.treatmentGradient} ${styles.weightTreatment}`} />
            </div>
          </div>
        </div>

        {/* Arrow 1 — weight CAUSES pressure */}
        <CascadeArrow which="1" />

        {/* Row 2 — Airway pressure */}
        <div className={styles.row}>
          <span className={`${styles.label} ${styles.pressureLabel}`}>AIRWAY PRESSURE</span>
          <div className={styles.barTrack}>
            <div className={`${styles.bar} ${styles.pressureBar}`}>
              <span className={styles.tonightGradient} />
              <span className={`${styles.treatmentGradient} ${styles.pressureTreatment}`} />
            </div>
          </div>
        </div>

        {/* Arrow 2 — pressure CAUSES interruptions */}
        <CascadeArrow which="2" />

        {/* Row 3 — Sleep interruptions (the effect) */}
        <div className={styles.row}>
          <span className={`${styles.label} ${styles.interruptionsLabel}`}>SLEEP INTERRUPTIONS</span>
          <div className={styles.barTrack}>
            <div className={`${styles.bar} ${styles.interruptionsBar}`}>
              <span className={styles.tonightGradient} />
              <span className={`${styles.treatmentGradient} ${styles.interruptionsTreatment}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Science lockup — pill capsule glyph + variant-specific paragraph. */}
      <div className={styles.scienceRow}>
        <svg width={18} height={18} viewBox="0 0 18 18" fill="none" aria-hidden="true" className={styles.scienceIcon}>
          <path
            d="M10.9507 0.35554C8.97503 0.350384 7.0233 1.43061 5.9398 3.3075L4.23909 6.2544L2.54183 9.1925C0.964873 11.9236 1.76409 15.4075 4.41441 16.938C7.06475 18.4677 10.4875 17.4219 12.0645 14.6907L13.742 11.7834C13.7497 11.7723 13.7574 11.7611 13.7652 11.7491C13.7738 11.7345 13.7815 11.7198 13.7884 11.7044C13.7901 11.7018 13.791 11.7001 13.7918 11.6975L15.4607 8.8056C17.0377 6.0754 16.229 2.59236 13.5795 1.0627C12.7511 0.584028 11.8487 0.358114 10.9507 0.35554ZM10.9207 2.18C11.5188 2.18258 12.1161 2.33555 12.6626 2.65093C14.4106 3.65983 14.9847 5.96391 13.8726 7.88957L12.6317 10.0397L6.28007 6.37276L7.521 4.22261C8.285 2.89832 9.60414 2.17399 10.9207 2.18ZM5.36126 7.9627L11.7138 11.6297L10.4763 13.7721C9.36423 15.6979 7.07836 16.3588 5.33112 15.3499C3.58316 14.3401 3.01254 12.0319 4.1237 10.106L5.36126 7.9627Z"
            fill="#7929ff"
          />
        </svg>
        <p className={styles.scienceText}>
          {isCompounded
            ? 'Compounded semaglutide drops can help reduce body weight, which may improve airway function and sleep quality in many people with sleep apnea.'
            : 'Zepbound® can help reduce body weight, which may improve airway function and sleep quality in many people with sleep apnea.'}
        </p>
      </div>

      <span className={styles.illustrativeNote}>Illustrative comparison for educational purposes</span>
    </div>
  );
};

/** Down-chevron arrow (vertical bar + V). Stroke color is animated via CSS
 *  keyframes targeting the path/polyline (idle white-55 → gold on down
 *  pulse → pink on up pulse), so we don't need React state here. */
const CascadeArrow = ({which}: {which: '1' | '2'}) => (
  <div className={`${styles.arrowWrap} ${which === '1' ? styles.arrowWrap1 : styles.arrowWrap2}`}>
    <svg
      width={14}
      height={22}
      viewBox="0 0 14 22"
      className={`${styles.arrow} ${which === '1' ? styles.arrow1 : styles.arrow2}`}
      aria-hidden="true"
    >
      <path d="M7 2 L 7 17" strokeWidth={1.8} strokeLinecap="round" fill="none" />
      <polyline points="2,12 7,20 12,12" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  </div>
);
